import posthog from 'posthog-js'
import {
  trackEnhancedError,
  trackEnhancedAPIError,
  createEnhancedErrorBoundaryHandler,
} from './enhanced-error-tracking'

declare global {
  interface Window {
    posthog?: typeof posthog
    enableDevRecording?: () => void
    disableDevRecording?: () => void
  }
}

export interface PostHogConfig {
  apiKey: string
  apiHost?: string
  enableHeatmaps?: boolean
  enableSessionRecording?: boolean
  enableErrorTracking?: boolean
}

// Shared predicate — drop high-volume noise that is not actionable.
// Used by the PostHog beforeSend hook and both global error handlers so the
// filter list can never drift between them.
// Input must already be trimmed and lowercased.
const isPosthogNoiseMessage = (normalizedMsg: string): boolean =>
  normalizedMsg === 'failed to fetch' ||
  normalizedMsg.includes('resizeobserver loop') ||
  normalizedMsg.includes('script error') ||
  normalizedMsg.startsWith('[cloudflare turnstile]')

export const initPostHog = (config: PostHogConfig) => {
  // Initialize PostHog (works in both dev and prod)
  // In development, events won't be sent unless explicitly enabled
  const isDevelopment = process.env.NODE_ENV === 'development'
  const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname.includes('.localhost'))

  if (!config.apiKey) {
    if (isDevelopment) console.warn('PostHog API key is missing — check VITE_POSTHOG_KEY.')
    return
  }

  posthog.init(config.apiKey, {
    api_host: config.apiHost || 'https://eu.i.posthog.com',

    // Internal staff tool analytics configuration
    autocapture: true, // Required for heatmaps + click tracking
    capture_pageview: false, // Manual page.viewed tracking in layout.component.jsx (with UUID stripping)
    // Disable capturing on localhost entirely — PostHog's SDK-level exception
    // auto-capture would otherwise bypass our custom isLocalhost() guard in
    // enhanced-error-tracking.ts and leak dev errors into Slack channels.
    // On all deployed environments (dev + prod), capturing is enabled so that
    // session recordings and error tracking work correctly.
    opt_out_capturing_by_default: isLocalhost,
    respect_dnt: false, // Internal staff tool — DNT would block analytics for staff users
    person_profiles: 'identified_only',

    // Enable session recording in all environments
    disable_session_recording: false,

    // Enhanced features
    enable_heatmaps: config.enableHeatmaps ?? true,

    // Session recording with privacy
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '[data-sensitive]',
      recordCrossOriginIframes: false,
    },

    // Feature flags
    bootstrap: {
      featureFlags: {
        // Preload critical flags
      },
    },

    // Error tracking with PostHog
    capture_dead_clicks: true,
    capture_performance: true,

    // Drop known-noisy exception events before ingestion so they never count
    // against the PostHog billing quota. This is the SDK-level counterpart to
    // the window.addEventListener filters below — belt-and-suspenders approach
    // that covers auto-captured exceptions as well as manual captures.
    before_send: (event) => {
      if (!event) return null
      if (event.event === '$exception') {
        const msg = String(event.properties?.['$exception_message'] ?? '')
          .trim()
          .toLowerCase()
        if (isPosthogNoiseMessage(msg)) return null
      }
      return event
    },
  })

  // Dev helpers — available via browser console
  if (typeof window !== 'undefined' && isDevelopment) {
    window.posthog = posthog
    window.enableDevRecording = () => {
      localStorage.setItem('enable_dev_recording', 'true')
      posthog.startSessionRecording()
    }
    window.disableDevRecording = () => {
      localStorage.removeItem('enable_dev_recording')
      posthog.stopSessionRecording()
    }
  }

  // Set up error tracking with PostHog (disabled on localhost)
  // Note: Enhanced error tracking is available via trackEnhancedError()
  // from enhanced-error-tracking.ts for manual error tracking with full context.
  // These global handlers provide basic automatic tracking for unhandled errors.
  if (config.enableErrorTracking && !isLocalhost) {
    // Capture unhandled errors
    window.addEventListener('error', (event) => {
      const msg = String(event.message || '')
        .trim()
        .toLowerCase()
      if (isPosthogNoiseMessage(msg)) return
      const error = event.error || new Error(event.message)
      trackEnhancedError(error, {
        metadata: {
          error_file: event.filename,
          error_line: event.lineno,
          error_column: event.colno,
        },
      })
    })

    // Capture unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      // Skip 401 errors — user is simply not authenticated yet
      if (event.reason?.response?.status === 401) {
        return
      }

      // Extract message — handle string rejections, Error objects, and GQty-style objects
      const rawMessage =
        event.reason instanceof Error
          ? event.reason.message
          : event.reason?.message || event.reason?.error || String(event.reason ?? '')

      const msg = String(rawMessage).trim().toLowerCase()
      if (!msg || msg === 'undefined' || msg === 'null' || isPosthogNoiseMessage(msg)) return

      const error = event.reason instanceof Error ? event.reason : new Error(rawMessage)
      error.name = 'UnhandledPromiseRejection'
      trackEnhancedError(error)
    })
  }
}

// Enhanced tracking with feature flag context
export const trackEvent = (
  eventName: string,
  properties?: Record<string, unknown>,
  options?: { feature_flag?: string }
) => {
  // Internal staff tool — no consent gate needed. Localhost opt-out prevents dev noise.
  if (!posthog || typeof posthog.capture !== 'function') {
    return
  }

  const enrichedProperties = {
    ...properties,
    // Add current feature flag if tracking within a flag
    current_feature_flag: options?.feature_flag,
    // Add session recording URL if available
    session_recording_url: posthog.get_session_replay_url
      ? posthog.get_session_replay_url()
      : undefined,
  }

  posthog.capture(eventName, enrichedProperties)
}

// Helper to track errors with context
// This is a legacy wrapper - prefer using trackEnhancedError() directly
export const trackError = (error: Error, context?: Record<string, unknown>) => {
  trackEnhancedError(error, {
    metadata: context,
  })
}

// Re-export enhanced error tracking functions for convenience
export { trackEnhancedError, trackEnhancedAPIError, createEnhancedErrorBoundaryHandler }
